import { useMemo } from 'react';

import { Box, Card, Link, List, ListIcon, ListItem, Tag, TagLabel, TagLeftIcon } from '@chakra-ui/react';

import { trackEvent } from '~/features/analytics/track-event';
import { REVIEW_MINIMUM_THRESHOLD } from '~/features/program/constants';
import { Program } from '~/features/program/models/program';
import { format, useFormatDate } from '~/hooks/use-format-date';

import {
  CalendarIcon,
  Difficulty1Icon,
  Difficulty2Icon,
  Difficulty3Icon,
  GraduationCapIcon,
  ICON_SIZE,
  OpenBookIcon,
  StarIcon,
  StopWatchIcon,
} from '../icons';

export type ProgramStatsCardProps = {
  updatedAt: string;
  reviewCount: number;
  semanticType: Program['semanticType'];
  difficulty: string | null;
  duration: string | null;
  reviewStarsAverage: number;
  programKey: string;
};
export function ProgramStatsCard({
  updatedAt,
  reviewCount,
  semanticType,
  difficulty,
  duration,
  reviewStarsAverage,
  programKey,
}: ProgramStatsCardProps) {
  const updatedAtDate = useFormatDate({ date: updatedAt, format: format.monthDayYearShort, forceUTC: true });
  const hasReviews = reviewCount >= REVIEW_MINIMUM_THRESHOLD;
  const programTypeLabel = useMemo(() => {
    switch (semanticType) {
      case 'Part':
      case 'Course':
        return 'Course';
      case 'Degree':
        return 'Nanodegree Program';
      default:
        return null;
    }
  }, [semanticType]);

  return (
    <Card
      as={List}
      bgColor='supportBlue.10'
      borderColor='supportBlue.20'
      borderRadius='md'
      borderWidth='1px'
      boxShadow='none'
      data-testid='program-stats-card'
      display='flex'
      flexDirection='row'
      flexWrap='wrap'
      gap={4}
      justifyContent='flex-start'
      listStyleType='none'
      p={{
        base: 3,
        md: 4,
      }}
    >
      <Tag key='program-type' as={ListItem} colorScheme={semanticType === 'Degree' ? 'orange' : 'purple'}>
        <TagLeftIcon
          as={semanticType === 'Degree' ? GraduationCapIcon : OpenBookIcon}
          color='inherit'
          height={ICON_SIZE}
          width={ICON_SIZE}
        />
        <TagLabel
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          {programTypeLabel}
        </TagLabel>
      </Tag>
      {!!difficulty && (
        <ListItem
          key='difficulty'
          alignItems='center'
          color='supportGray.100'
          display='flex'
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          {['Beginner', 'Fluency', 'Discovery'].includes(difficulty) && (
            <ListIcon as={Difficulty1Icon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          )}
          {difficulty === 'Intermediate' && (
            <ListIcon as={Difficulty2Icon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          )}
          {difficulty === 'Advanced' && (
            <ListIcon as={Difficulty3Icon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          )}
          {difficulty}
        </ListItem>
      )}
      {!!duration && (
        <ListItem
          key='duration'
          alignItems='center'
          color='supportGray.100'
          display='flex'
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          <ListIcon as={StopWatchIcon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          {duration}
        </ListItem>
      )}
      {hasReviews && (
        <ListItem
          key='reviews'
          alignItems='center'
          color='supportGray.100'
          display='flex'
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          <ListIcon as={StarIcon} color='primary.orange' height={ICON_SIZE} width={ICON_SIZE} />
          <Box as='span'>
            <Box as='b' color='supportOrange.70' fontWeight='600'>
              {reviewStarsAverage}
            </Box>{' '}
            (
            <Link
              aria-label={`${reviewCount} reviews`}
              href='#reviews'
              onClick={() => {
                trackEvent({
                  type: 'click',
                  label: 'ratings_clicked',
                  name: 'Product Page CTA Clicked',
                  additionalAttributes: {
                    product_ids: [programKey],
                  },
                });
              }}
            >
              {reviewCount}
            </Link>
            )
          </Box>
        </ListItem>
      )}
      {!!updatedAtDate && (
        <ListItem
          key='updated-at'
          alignItems='center'
          color='supportGray.100'
          display='flex'
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          <ListIcon as={CalendarIcon} color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
          <Box as='span'>Updated: {updatedAtDate}</Box>
        </ListItem>
      )}
    </Card>
  );
}
